import { Injectable, signal, computed, effect, inject, untracked } from '@angular/core';
import { Router, NavigationEnd, NavigationStart } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { Token, Farm, StakingPool, MarketStats, MarketToken, TokenDetailStats, TokenAbout, TokenTransaction, LaunchpadProject, HomeCmsContent } from '../models/types';
import { DEFAULT_HOME_CMS, HOME_CMS_IDENTIFIER, parseHomeCmsContent } from '../config/home-cms.defaults';
import { CmsApiResponse, CmsPageService } from './cms-page.service';
import { ActivityService } from './activity.service';
import { Web3Service, appkit } from './web3.service';
import { AuthService } from './auth.service';
import { RoutingService } from './routing.service';
import { ethers } from 'ethers';
import { NETWORKS } from '../config/network.config';
import { NgZone } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class AppStateService {
  private ws: WebSocket | null = null;
  private isWsConnected = false;

  isDarkMode = signal<boolean>(true);

  isWalletConnected = signal<boolean>(false);
  walletAddress = signal<string>('');
  walletBalance = signal<number>(12.45);
  selectedWalletProvider = signal<string>('');
  showConnectModal = signal<boolean>(false);

  private web3Service = inject(Web3Service);
  private authService = inject(AuthService);
  private routingService = inject(RoutingService);
  private http = inject(HttpClient);
  private cmsPageService = inject(CmsPageService);
  private activityService = inject(ActivityService);
  private zone = inject(NgZone);

  // Site Settings
  siteSettings = signal<{
    xUrl: string;
    telegramUrl: string;
    discordUrl: string;
    githubUrl: string;
    youtubeUrl?: string;
    networkEnvironment?: string;
  }>({
    xUrl: '',
    telegramUrl: '',
    discordUrl: '',
    githubUrl: '',
    youtubeUrl: '',
    networkEnvironment: 'TESTNET',
  });

  homeCms = signal<HomeCmsContent>(DEFAULT_HOME_CMS);
  homeCmsLoaded = signal(false);

  selectedNetworkId = signal<string>('bnb');
  handleNetworkChanged(id: string) {
    if (this.selectedNetworkId() === id) return;
    this.selectedNetworkId.set(id);

    if (!this.web3Service.isConnected()) {
      const networkMap: Record<string, number> = { 'eth': 1, 'bnb': 56, 'matic': 137, 'sepolia': 11155111, 'amoy': 80002 };
      if (environment.networkMode === 'TESTNET') {
        networkMap['bnb'] = 97;
        networkMap['eth'] = 11155111;
        networkMap['matic'] = 80002;
      }
      if (networkMap[id]) {
        this.web3Service.chainId.set(networkMap[id]);
      }
    }

    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.setItem('dex_last_network', id);
        const networkMap: Record<string, string> = { 'eth': 'ETH', 'bnb': 'BSC', 'matic': 'POLYGON', 'sepolia': 'ETH' };
        const activeNetwork = networkMap[id] || 'BSC';
        const cachedTokens = localStorage.getItem(`dex_cached_tokens_${activeNetwork}`);
        if (cachedTokens) {
          const parsedTokens = JSON.parse(cachedTokens);
          if (Array.isArray(parsedTokens) && parsedTokens.length > 0) {
            this.tokens.set(parsedTokens);
          } else {
            this.tokens.set([]);
          }
        } else {
          this.tokens.set([]);
        }
      } catch (e) { }
    }

    const tks = this.tokens();
    if (tks.length > 0) {
      this.fromToken.set(tks[0]);
      this.toToken.set(null);
    } else {
      this.fromToken.set({
        symbol: '---',
        name: 'Select Token',
        pairName: '',
        rate: 0,
        change: 0,
        color: '#333',
        logoUrl: '',
      } as Token);
      this.toToken.set(null);
    }

    this.fromAmount.set('');
    this.toAmount.set('');
    this.currentSwapRoute.set(null);
    this.exchangeRate.set('0.00');

    this.fetchTokens();
  }

  async selectNetwork(id: string) {
    if (this.web3Service.isConnected()) {
      try {
        await this.web3Service.switchNetwork(id);
      } catch (err: any) {
        console.error(err);
        this.triggerNotification(this.handleError(err, 'Error switching network'), false);
        return;
      }
    }
    const names: Record<string, string> = {
      bnb: 'BNB',
      eth: 'ETH',
      sepolia: 'Sepolia',
      matic: 'Polygon',
    };
    this.triggerNotification(`Switched to ${names[id]} network!`);
    this.handleNetworkChanged(id);
  }

  tokens = signal<Token[]>([]);

  tokenBalances = signal<Record<string, string>>({});
  isBalancesLoading = signal<boolean>(false);

  fromToken = signal<any>({ symbol: '---', name: 'Select Token', rate: 0, change: 0, color: '#333', logoUrl: '' });
  toToken = signal<any>(null);

  fromAmount = signal<string>('');
  toAmount = signal<string>('');
  slippageTolerance = signal<number>(0.5);
  showSlippageDropdown = signal<boolean>(false);

  currentSwapRoute = signal<import('./routing.service').RouteQuote | null>(null);
  isFetchingQuote = signal<boolean>(false);
  isSwapping = signal<boolean>(false);
  priceImpact = signal<string>('0.00');
  networkFeeUSD = signal<string>('0.00');
  minimumReceived = signal<string>('0.00');
  exchangeRate = signal<string>('0.00');

  showTokenModal = signal<boolean>(false);
  tokenModalTarget = signal<'from' | 'to'>('from');
  tokenSearchQuery = signal<string>('');

  tokenModalFilter = signal<string>('all');
  favoriteTokens = signal<string[]>([]);

  filteredTokens = computed(() => {
    const query = this.tokenSearchQuery().toLowerCase().trim();
    const target = this.tokenModalTarget();
    const currentFrom = this.fromToken();
    const filter = this.tokenModalFilter();
    const favorites = this.favoriteTokens();

    let list = this.tokens();

    if (filter === 'favorites') {
      list = list.filter(t => favorites.includes(t.symbol));
    }

    if (query) {
      list = list.filter(
        (t) =>
          t.symbol.toLowerCase().includes(query) ||
          t.name.toLowerCase().includes(query) ||
          (t.address && t.address.toLowerCase().includes(query)),
      );
    }

    const to = this.toToken();
    if (target === 'from' && to) {
      list = list.filter(t => t.symbol !== to.symbol);

      if (to.address?.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' && to.wrapAddress) {
        const wrapIndex = list.findIndex(t => t.address?.toLowerCase() === to.wrapAddress!.toLowerCase());
        if (wrapIndex !== -1) {
          const wrapToken = list.splice(wrapIndex, 1)[0];
          list.unshift(wrapToken);
        }
      }
    }
    const from = this.fromToken();
    if (target === 'to' && from) {
      list = list.filter(t => t.symbol !== from.symbol);

      // Only show tokens that are paired with the fromToken
      if (from.pairs && from.pairs.length > 0) {
        const validAddresses = from.pairs.map((p: any) => p.tokenAddress?.toLowerCase());
        if (from.address?.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' && from.wrapAddress) {
          validAddresses.push(from.wrapAddress.toLowerCase());
        }
        list = list.filter(t => t.address && validAddresses.includes(t.address.toLowerCase()));
      } else {
        if (from.address?.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' && from.wrapAddress) {
          list = list.filter(t => t.address?.toLowerCase() === from.wrapAddress!.toLowerCase());
        } else {
          list = [];
        }
      }

      if (from.address?.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee' && from.wrapAddress) {
        const wrapIndex = list.findIndex(t => t.address?.toLowerCase() === from.wrapAddress!.toLowerCase());
        if (wrapIndex !== -1) {
          const wrapToken = list.splice(wrapIndex, 1)[0];
          list.unshift(wrapToken);
        }
      }
    }

    return list;
  });

  toggleFavoriteToken(symbol: string): boolean {
    const current = this.favoriteTokens();
    let isAdded = false;
    if (current.includes(symbol)) {
      this.favoriteTokens.set(current.filter(s => s !== symbol));
    } else {
      this.favoriteTokens.set([...current, symbol]);
      isAdded = true;
    }
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('dex_favorite_tokens', JSON.stringify(this.favoriteTokens()));
    }
    return isAdded;
  }

  tokenFilterTab = signal<'trending' | 'gainers'>('trending');

  topTokens = signal<Token[]>([]);

  displayTokens = computed(() => {
    return this.topTokens();
  });

  farms = signal<Farm[]>([]);
  stakingPools = signal<StakingPool[]>([]);

  claimableRewards = signal<number>(0.0);
  portfolioBalances = signal<
    { symbol: string; amount: number; valueUsd: number; color: string; percentage: number }[]
  >([]);

  launchpadProjects = signal<LaunchpadProject[]>([]);

  marketStats = signal<MarketStats[]>([]);

  marketTokens = signal<MarketToken[]>([]);

  tokenDetailStats = signal<TokenDetailStats>({
    tvl: '$0',
    marketCap: '$0',
    fdv: '$0',
    volume1D: '$0',
    high52W: '$0',
    low52W: '$0',
  });

  tokenAbout = signal<TokenAbout>({
    description:
      'USDC is a fully collateralized US dollar stablecoin. USDC is the bridge between dollars and trading on cryptocurrency exchanges. The technology behind CENTRE makes it possible to exchange value between people, businesses and financial institutions just like email between mail services and texts...',
    address: '0xa0b8...eb48',
    explorer: 'https://etherscan.io/',
    website: 'https://www.circle.com/en/usdc',
  });

  tokenTransactions = signal<TokenTransaction[]>([]);

  commitLaunchpadAmount = signal<string>('');
  showLaunchpadModal = signal<boolean>(false);
  selectedLaunchpadProject = signal<LaunchpadProject | null>(null);

  activeFeatureTab = signal<string>('swap');
  activeNavLink = signal<string>('home');

  private router = inject(Router);

  navLinks = [
    { label: 'Swap', id: 'swap' },
    { label: 'Explore', id: 'explore' },
    { label: 'Yield Farming', id: 'yield-farming' },
    { label: 'Staking', id: 'staking' },
    { label: 'Liquidity', id: 'liquidity' },
    { label: 'Portfolio', id: 'portfolio' }
  ];

  footerProducts = [
    { label: 'Swap', id: 'swap' },
    { label: 'Liquidity', id: 'liquidity' },
    { label: 'Farms', id: 'farms' },
    { label: 'Staking', id: 'staking' }
  ];

  footerCompany = [
    { label: 'About', id: 'about' },
    { label: 'Advantages', id: 'advantages' },
    { label: 'Bankless', id: 'bankless' },
    { label: 'Contact', id: 'contact' },
  ];

  footerLegal = [
    { label: 'Privacy Policy', id: 'privacy-policy' },
    { label: 'Terms of Service', id: 'terms-and-conditions' },
  ];

  footerCommunity = computed(() => [
    { label: 'Twitter', url: this.siteSettings().xUrl },
    { label: 'Telegram', url: this.siteSettings().telegramUrl },
    { label: 'Discord', url: this.siteSettings().discordUrl },
    { label: 'YouTube', url: this.siteSettings().youtubeUrl || 'https://youtube.com' }
  ]);

  // Success Notification for simulated actions
  notificationMessage = signal<string>('');
  showNotification = signal<boolean>(false);
  notificationIsSuccess = signal<boolean>(true);

  // Staking Modal State
  showStakingModal = signal<boolean>(false);
  stakingAmountInput = signal<string>('');
  selectedStakedPool = signal<StakingPool | null>(null);

  private skipNextClear = false;

  constructor() {
    this.web3Service.autoConnect();

    if (typeof window !== 'undefined' && window.localStorage) {
      const storedFavorites = localStorage.getItem('dex_favorite_tokens');
      if (storedFavorites) {
        try {
          this.favoriteTokens.set(JSON.parse(storedFavorites));
        } catch (e) { }
      }
    }

    // Subscribe to Web3Service connection changes
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const lastNetwork = localStorage.getItem('dex_last_network');
        console.log('[DEBUG] AppStateService loaded lastNetwork:', lastNetwork);
        if (lastNetwork) {
          this.selectedNetworkId.set(lastNetwork);
          // Sync web3Service chainId if wallet is not connected
          console.log('[DEBUG] AppStateService isConnected:', this.web3Service.isConnected());
          if (!this.web3Service.isConnected()) {
            const networkMap: Record<string, number> = { 'eth': 1, 'bnb': 56, 'matic': 137, 'sepolia': 11155111, 'amoy': 80002 };
            if (environment.networkMode === 'TESTNET') {
              networkMap['bnb'] = 97;
              networkMap['eth'] = 11155111;
              networkMap['matic'] = 80002;
            }
            if (networkMap[lastNetwork]) {
              console.log('[DEBUG] AppStateService forcing web3Service.chainId to:', networkMap[lastNetwork]);
              this.web3Service.chainId.set(networkMap[lastNetwork]);
            }
          }
        }

        const networkMap: Record<string, string> = {
          eth: 'ETH',
          bnb: 'BSC',
          matic: 'POLYGON',
          sepolia: 'ETH',
        };
        const activeNetwork = networkMap[this.selectedNetworkId()] || 'BSC';
        const cachedTokens = localStorage.getItem(`dex_cached_tokens_${activeNetwork}`);
        if (cachedTokens) {
          const parsedTokens = JSON.parse(cachedTokens);
          if (Array.isArray(parsedTokens) && parsedTokens.length > 0) {
            this.tokens.set(parsedTokens);

          }
        }
      } catch (e) {
        console.log('ERROR', e);
      }
    }

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (this.skipNextClear) {
          this.skipNextClear = false;
        } else {
          this.fromAmount.set('');
          this.toAmount.set('');
        }
      }

      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects;
        if (url.includes('/swap')) {
          this.activeNavLink.set('swap');
          this.activeFeatureTab.set('swap');
        } else if (url.includes('/launchpad')) {
          this.activeNavLink.set('launchpad');
        } else if (url.includes('/liquidity')) {
          this.activeNavLink.set('liquidity');
          this.activeFeatureTab.set('liquidity');
        } else if (url.includes('/staking')) {
          this.activeNavLink.set('staking');
          this.activeFeatureTab.set('staking');
        } else if (url.includes('/yield-farming')) {
          this.activeNavLink.set('yield-farming');
          this.activeFeatureTab.set('farms');
        } else if (url.includes('/portfolio')) {
          this.activeNavLink.set('portfolio');
        } else if (url.includes('/explore')) {
          this.activeNavLink.set('explore');
        } else if (url.includes('/advantages')) {
          this.activeNavLink.set('advantages');
        } else {
          if (
            ['swap', 'launchpad', 'portfolio', 'liquidity', 'staking', 'explore', 'advantages'].includes(
              this.activeNavLink(),
            )
          ) {
            this.activeNavLink.set('home');
          }
        }
      }
    });
    // Sync Auth/Web3Service state to AppState
    effect(() => {
      this.isWalletConnected.set(this.web3Service.isConnected());

      const cId = this.web3Service.chainId();
      let newNetwork = '';
      const networks = NETWORKS;
      for (const key in networks) {
        if (networks[key].chainId === cId) {
          newNetwork = key;
          break;
        }
      }

      if (newNetwork) {
        untracked(() => {
          this.handleNetworkChanged(newNetwork);
        });
      }

      const addr = this.web3Service.address();
      if (addr) {
        this.walletAddress.set(`${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}`);

        untracked(() => {
          console.log(`[Debug] Effect triggered fetchBalances. tokens() length: ${this.tokens().length}`);
          this.fetchBalances();
        });
      } else {
        this.walletAddress.set('');
        this.tokenBalances.set({});
      }

      this.walletBalance.set(this.web3Service.balance());
      this.selectedWalletProvider.set(this.web3Service.connectedProvider());
    });


    setInterval(() => {
      if (this.isWalletConnected()) {
        this.claimableRewards.update((val) => val + 0.0002345);
      }
    }, 1000);


    effect(
      () => {
        const filter = this.tokenFilterTab();
        untracked(() => {
          this.fetchTopTokens(filter);
        });
      },
      { allowSignalWrites: true },
    );

    setInterval(() => {
      const isSwapRoute = this.activeNavLink() === 'swap';
      const hasAmount = parseFloat(this.fromAmount()) > 0 || parseFloat(this.toAmount()) > 0;
      if (isSwapRoute && hasAmount && !this.isFetchingQuote() && !this.isSwapping()) {
        untracked(() => {
          this.calculateRoute(true);
        });
      }
    }, 8000);

    this.fetchSiteSettings();
    this.fetchTokens();
    this.fetchTopFarms();
    this.fetchTopStaking();
    this.connectMarketDataWebSocket();
  }

  async fetchTokens() {
    console.log(`[Debug] fetchTokens started...`);
    const start = performance.now();

    try {
      const networkMap: Record<string, string> = {
        eth: 'ETH',
        bnb: 'BSC',
        matic: 'POLYGON',
        sepolia: 'ETH',
      };
      const activeNetwork = networkMap[this.selectedNetworkId()] || 'BSC';
      console.log(

      );
      const res: any = await firstValueFrom(
        this.http.get(`${environment.apiUrl}/user/liquidity/tokens?network=${activeNetwork}`),
      );

      let externalTokensMap: Record<string, any> = {};
      try {
        const extRes: any = await firstValueFrom(this.http.get(`${environment.apiUrl}/external/tokens`));
        if (extRes && extRes.data) {
          extRes.data.forEach((extT: any) => {
            if (extT.address) {
              externalTokensMap[extT.address.toLowerCase()] = extT;
            }
          });
        }
      } catch (e) {
        console.error('Failed to fetch external prices', e);
      }

      if (res && res.status && res.data) {
        const dynamicTokens = res.data.map((t: any) => {
          const ext = t.address ? externalTokensMap[t.address.toLowerCase()] : null;
          const price = ext && ext.priceUsd > 0 ? ext.priceUsd : (t.priceUsd || 0);
          const change = ext ? (ext.priceChange1d || 0) : 0;

          return {
            symbol: t.symbol,
            name: t.name,
            pairName: t.symbol + ' / USD',
            rate: price,
            change: change,
            color: t.logoUrl ? 'transparent' : '#888888',
            logoUrl: t.logoUrl,
            address: t.address,
            decimals: t.decimals || 18,
            pairs: t.pairs,
          };
        });

        const nativeMap: any = {
          'eth': { symbol: 'ETH', name: 'Ethereum', address: '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee', wrapSymbol: 'WETH', logoUrl: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png' },
          'bsc': { symbol: 'BNB', name: 'BNB', address: '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee', wrapSymbol: 'WBNB', logoUrl: 'https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png' },
          'polygon': { symbol: 'MATIC', name: 'Polygon', address: '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee', wrapSymbol: 'WMATIC', logoUrl: 'https://assets.coingecko.com/coins/images/4713/small/matic-token-icon.png' },
          'arbitrum': { symbol: 'ETH', name: 'Ethereum', address: '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee', wrapSymbol: 'WETH', logoUrl: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png' },
          'base': { symbol: 'ETH', name: 'Ethereum', address: '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee', wrapSymbol: 'WETH', logoUrl: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png' }
        };

        const nativeInfo = nativeMap[activeNetwork.toLowerCase()];
        if (nativeInfo) {
          const wrapped = dynamicTokens.find((t: any) => t.symbol?.toUpperCase() === nativeInfo.wrapSymbol || (nativeInfo.wrapSymbol === 'WMATIC' && t.symbol?.toUpperCase() === 'WPOL'));
          if (wrapped) {
            const nativeClone = {
              ...wrapped,
              symbol: nativeInfo.symbol,
              name: nativeInfo.name,
              pairName: nativeInfo.symbol + ' / USD',
              address: nativeInfo.address,
              logoUrl: nativeInfo.logoUrl,
              color: 'transparent',
              wrapAddress: wrapped.address // Store wrapped address for router execution
            };
            dynamicTokens.unshift(nativeClone);
          }
        }

        if (dynamicTokens.length > 0) {
          if (typeof window !== 'undefined' && window.localStorage) {
            try {
              localStorage.setItem(
                `dex_cached_tokens_${activeNetwork}`,
                JSON.stringify(dynamicTokens),
              );
            } catch (e) {
              console.log("ERROR in Local storage", e)
            }
          }

          this.tokens.set(dynamicTokens);

          const currentFrom = this.fromToken();
          const currentTo = this.toToken();

          const currentNetworkId = this.selectedNetworkId();
          const isNetworkSwitch = (this as any)._lastFetchedNetworkId !== currentNetworkId;
          (this as any)._lastFetchedNetworkId = currentNetworkId;

          const isMissing = !currentFrom || currentFrom.symbol === '---' || !dynamicTokens.some((t: any) => t.address?.toLowerCase() === currentFrom.address?.toLowerCase() || t.symbol === currentFrom.symbol);

          if (isNetworkSwitch || isMissing) {
            const isAddLiquidity = typeof window !== 'undefined' && (window.location.pathname.includes('/liquidity/add') || window.location.pathname.includes('/liquidity/new'));
            let defaultFrom;

            if (isAddLiquidity) {
              defaultFrom = dynamicTokens.find((t: any) => t.address?.toLowerCase() !== '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee') || dynamicTokens[0];
            } else {
              defaultFrom = dynamicTokens.find((t: any) => t.address?.toLowerCase() === '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee') || dynamicTokens[0];
            }
            let defaultTo = dynamicTokens.find((t: any) => ['WETH', 'WBNB', 'WPOL', 'WMATIC'].includes(t.symbol?.toUpperCase()));

            if (defaultFrom) {
              this.fromToken.set(defaultFrom);
            }
            if (defaultTo) {
              this.toToken.set(defaultTo);
            } else if (dynamicTokens.length > 1) {
              this.toToken.set(dynamicTokens.find((t: any) => t.address !== defaultFrom?.address) || dynamicTokens[1]);
            }
          }
        } else {
          console.log(`[Debug] Dynamic tokens fetch failed or empty`);
          if (this.tokens().length === 0) {
            this.tokens.set([]);
          }
          this.fromToken.set({
            symbol: '---',
            name: 'Select Token',
            pairName: '',
            rate: 0,
            change: 0,
            color: '#333',
          } as Token);
          this.toToken.set(null as any);
        }

        console.log(`[Debug] fetchTokens completed. tokens() length: ${this.tokens().length}. Calling fetchBalances()...`);
        if (this.isWalletConnected()) {
          this.fetchBalances();
        }
      }
    } catch (err) {
      console.error('Error fetching dynamic tokens', err);
    }
  }

  async fetchTopTokens(filter: 'trending' | 'gainers') {
    try {
      const res: any = await firstValueFrom(
        this.http.get(`${environment.apiUrl}/user/tokens/top?filter=${filter}`),
      );
      if (res && res.status && res.data) {
        const mapped = res.data.map((t: any) => ({
          symbol: t.symbol,
          name: t.name,
          pairName: `${t.symbol} / USDT`,
          rate: t.priceUsd || 0.0,
          change: t.priceChange24h || 0.0,
          color: t.logoUrl ? 'transparent' : this.getRandomColor(t.symbol),
          logoUrl: t.logoUrl,
          address: t.address,
          decimals: t.decimals || 18,
        }));
        this.topTokens.set(mapped);
      }
    } catch (err) {
      console.error('Error fetching top tokens', err);
    }
  }

  async fetchTopFarms() {
    try {
      const res: any = await firstValueFrom(this.http.get(`${environment.apiUrl}/user/farms/top`));
      if (res && res.status && res.data) {
        const globalTokens = this.tokens();
        const mappedFarms = res.data.map((f: any) => {
          const t0 = globalTokens.find(t => t.symbol === f.token0Symbol);
          const t1 = globalTokens.find(t => t.symbol === f.token1Symbol);
          return {
            pair: f.pairName,
            token0Image: t0?.logoUrl || '',
            token1Image: t1?.logoUrl || '',
            token1Color: this.getRandomColor(f.token0Symbol || 'A'),
            token2Color: this.getRandomColor(f.token1Symbol || 'B'),
            earned: 0.0,
            apr: f.apr,
          };
        });
        this.farms.set(mappedFarms);
      }
    } catch (err) {
      console.error('Error fetching top farms:', err);
    }
  }

  async fetchTopStaking() {
    try {
      const res: any = await firstValueFrom(
        this.http.get(`${environment.apiUrl}/user/staking/top`),
      );
      if (res && res.status && res.data) {
        const globalTokens = this.tokens();
        const mappedStaking = res.data.map((s: any) => {
          const t = globalTokens.find(tok => tok.symbol === s.stakingTokenSymbol);
          return {
            name: `${s.stakingTokenSymbol} Staking`,
            tokenColor: this.getRandomColor(s.stakingTokenSymbol),
            tokenSymbol: s.stakingTokenSymbol,
            logoUrl: t?.logoUrl || '',
            apr: s.apr,
            tvl: this.formatTvl(s.totalStaked, s.stakingTokenSymbol),
          };
        });
        this.stakingPools.set(mappedStaking);
      }
    } catch (err) {
      console.error('Error fetching top staking pools:', err);
    }
  }

  wsReconnectAttempts = 0;
  maxWsReconnectAttempts = 3;

  connectMarketDataWebSocket() {
    if (this.wsReconnectAttempts >= this.maxWsReconnectAttempts) {
      console.log('Max WebSocket reconnect attempts reached. Using HTTP polling exclusively.');
      return;
    }

    try {
      const wsUrl = environment.apiUrl.replace(/^http/, 'ws') + '/ws/market-data';
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('Market data WebSocket connected');
        this.isWsConnected = true;
      };

      this.ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'price-updates' && Array.isArray(msg.data)) {
            this.updatePrices(msg.data);
          }
        } catch (err) {
          console.error('Error parsing market data WebSocket message', err);
        }
      };

      this.ws.onclose = () => {
        if (this.wsReconnectAttempts < this.maxWsReconnectAttempts) {
          this.wsReconnectAttempts++;
          console.log(`Market data WebSocket disconnected. Reconnecting in 5s... (Attempt ${this.wsReconnectAttempts}/${this.maxWsReconnectAttempts})`);
          setTimeout(() => this.connectMarketDataWebSocket(), 5000);
        }
        this.isWsConnected = false;
      };

      this.ws.onerror = (err) => {
        // Use console.warn instead of console.error to prevent red error logs for graceful fallback
        console.warn('Market data WebSocket connection failed. Falling back to HTTP polling...');
        this.isWsConnected = false;
        this.ws?.close();
      };
    } catch (e) {
      console.warn('Failed to create WebSocket connection, falling back to HTTP polling.', e);
      this.isWsConnected = false;

      if (this.wsReconnectAttempts < this.maxWsReconnectAttempts) {
        this.wsReconnectAttempts++;
        setTimeout(() => this.connectMarketDataWebSocket(), 10000);
      }
    }

    // HTTP Fallback polling
    if (!this.wsFallbackInterval) {
      this.wsFallbackInterval = setInterval(() => {
        if (!this.isWsConnected) {
          untracked(() => {
            this.fetchTokens();
            this.fetchTopTokens(this.tokenFilterTab());
          });
        }
      }, 15000);
    }
  }

  private wsFallbackInterval: any;

  updatePrices(updatedTokens: any[]) {
    // 1. Update main tokens signal (for swaps, dropdowns, etc.)
    this.tokens.update((list) =>
      list.map((t) => {
        const match = updatedTokens.find(
          (ut) => ut.address?.toLowerCase() === t.address?.toLowerCase(),
        );
        if (match) {
          return {
            ...t,
            rate: match.priceUsd || t.rate,
            change: match.priceChange24h !== undefined ? match.priceChange24h : t.change,
          };
        }
        return t;
      }),
    );

    this.topTokens.update((list) =>
      list.map((t) => {
        const match = updatedTokens.find(
          (ut) => ut.address?.toLowerCase() === t.address?.toLowerCase(),
        );
        if (match) {
          return {
            ...t,
            rate: match.priceUsd || t.rate,
            change: match.priceChange24h !== undefined ? match.priceChange24h : t.change,
          };
        }
        return t;
      }),
    );
  }

  formatTvl(totalStaked: string, symbol: string): string {
    const val = parseFloat(totalStaked) || 0;
    if (val >= 1e6) {
      return `$${(val / 1e6).toFixed(1)}M`;
    }
    if (val >= 1e3) {
      return `$${(val / 1e3).toFixed(1)}K`;
    }
    return `$${val.toFixed(0)}`;
  }

  getRandomColor(symbol: string): string {
    const hash = symbol.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const colors = ['#f3ba2f', '#627eea', '#2775ca', '#d1884f', '#ff5722', '#03a9f4', '#e84142'];
    return colors[hash % colors.length];
  }

  async fetchSiteSettings() {
    try {
      const res: any = await firstValueFrom(
        this.http.get(`${environment.apiUrl}/user/site-setting`),
      );
      if (res && res.status) {
        this.siteSettings.set(res.data);

        if (res.data.networkEnvironment) {
          // environment is driven entirely by build target now.
        }

        if (res.data.networkEnvironment === 'TESTNET') {
          if (this.selectedNetworkId() !== 'eth' && this.selectedNetworkId() !== 'bnb' && this.selectedNetworkId() !== 'matic') {
            this.selectedNetworkId.set('bnb'); // Default
          }
        }
      }
    } catch (err) {
      console.error('Error fetching site settings', err);
    }
  }

  applyHomeCmsResponse(res: CmsApiResponse | null) {
    this.homeCmsLoaded.set(true);

    if (res?.status && res.data?.content) {
      const cms = parseHomeCmsContent(res.data.content);
      this.homeCms.set(cms);
      this.cmsPageService.applyPageSeo(cms.seo);
      return;
    }

    this.homeCms.set(DEFAULT_HOME_CMS);
    this.cmsPageService.applyPageSeo(DEFAULT_HOME_CMS.seo);
  }

  async fetchHomeCms() {
    try {
      const res = await firstValueFrom(this.cmsPageService.fetchByIdentifier(HOME_CMS_IDENTIFIER));
      this.applyHomeCmsResponse(res);
    } catch (err) {
      console.error('Error fetching home CMS', err);
    }
  }

  openEcosystemCard(index: number) {
    const tabs = ['swap', 'liquidity', 'yield-farming', 'staking'] as const;
    const tab = tabs[index];
    if (tab) {
      this.selectTab(tab);
    }
  }

  // Action methods
  toggleTheme() {
    this.isDarkMode.update((prev) => !prev);
  }

  openConnectModal() {
    // If Web3 is already connected but backend auth is missing (e.g. they rejected it earlier), re-prompt signature
    if (this.web3Service.isConnected() && !this.authService.isAuthenticated()) {
      const addr = this.web3Service.address();
      if (addr) {
        this.authService.login(addr).catch((err) => {
          console.error('Manual login prompt failed:', err);
        });
        return;
      }
    }
    this.authService.promptForLoginOnConnect = true;
    appkit.open();
  }

  closeConnectModal() {
    appkit.close();
  }

  async connectWallet() {
    this.openConnectModal();
  }

  async disconnectWallet() {
    await this.web3Service.disconnect();
    this.authService.logout();
    this.triggerNotification('Wallet disconnected');
  }

  triggerNotification(message: string, isSuccess: boolean = true) {
    this.zone.run(() => {
      this.notificationMessage.set(message);
      this.notificationIsSuccess.set(isSuccess);
      this.showNotification.set(true);
      setTimeout(() => {
        this.zone.run(() => {
          this.showNotification.set(false);
        });
      }, 4000);
    });
  }

  async fetchBalances(silent: boolean = false) {
    console.log(`[Debug] fetchBalances called. silent: ${silent}, isConnected: ${this.web3Service.isConnected()}, tokens() length: ${this.tokens().length}, tokenBalances keys: ${Object.keys(this.tokenBalances()).length}`);
    if (!this.web3Service.isConnected()) return;
    try {
      if (!silent && Object.keys(this.tokenBalances()).length === 0) {
        console.log(`[Debug] Setting isBalancesLoading = true`);
        this.zone.run(() => {
          this.isBalancesLoading.set(true);
        });
      }
      const balances: Record<string, string> = { ...this.tokenBalances() };
      const walletAddr = this.web3Service.address();
      const provider = this.web3Service.getReadProvider();
      const startTime = performance.now();

      const allTokens = this.tokens();

      const MULTICALL3_ADDRESS = '0xcA11bde05977b3631167028862bE2a173976CA11';
      const MULTICALL_ABI = ['function tryAggregate(bool requireSuccess, tuple(address target, bytes callData)[] calls) public view returns (tuple(bool success, bytes returnData)[] returnData)'];
      const erc20Iface = new ethers.Interface(['function balanceOf(address) view returns (uint256)']);

      let multicallContract;
      try {
        multicallContract = new ethers.Contract(MULTICALL3_ADDRESS, MULTICALL_ABI, provider);
      } catch (e) {
        console.warn('Multicall setup failed, falling back', e);
      }

      const calls: { target: string; callData: string }[] = [];
      const tokenMap: any[] = [];
      const NATIVE = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';

      for (const token of allTokens) {
        if (!token.address) continue;
        if (token.address.toLowerCase() === NATIVE) continue; // Skip native token for multicall

        try {
          calls.push({
            target: token.address,
            callData: erc20Iface.encodeFunctionData('balanceOf', [walletAddr])
          });
          tokenMap.push(token);
        } catch (e) {
          console.warn('Failed to encode multicall for token', token.symbol);
        }
      }

      // Fetch native balance and multicall concurrently (2 RPC calls total)
      const [nativeBalance, multicallResults] = await Promise.all([
        provider.getBalance(walletAddr).catch(() => 0n),
        (multicallContract && calls.length > 0) ? multicallContract['tryAggregate'](false, calls).catch(() => []) : Promise.resolve([])
      ]);

      // Set Native Balance
      balances[NATIVE] = parseFloat(ethers.formatEther(nativeBalance)).toFixed(8);

      // Parse Multicall Results
      for (let i = 0; i < multicallResults.length; i++) {
        const result = multicallResults[i];
        const token = tokenMap[i];
        try {
          if (result.success && result.returnData !== '0x') {
            const decodedBal = erc20Iface.decodeFunctionResult('balanceOf', result.returnData)[0];
            balances[token.address.toLowerCase()] = parseFloat(ethers.formatUnits(decodedBal, token.decimals || 18)).toFixed(8);
          } else {
            balances[token.address.toLowerCase()] = '0.00000000';
          }
        } catch (e) {
          balances[token.address.toLowerCase()] = '0.00000000';
        }
      }

      const endTime = performance.now();
      console.log(`[Performance] Fetched ${this.tokens().length} token balances concurrently in ${Math.round(endTime - startTime)}ms`);

      this.zone.run(() => {
        this.tokenBalances.set(balances);
      });
    } catch (error) {
      console.error('Error fetching token balances', error);
    } finally {
      console.log(`[Debug] fetchBalances finally block. Setting isBalancesLoading = false`);
      this.zone.run(() => {
        this.isBalancesLoading.set(false);
      });
    }
  }

  getFormattedBalance(address: string | undefined): string {
    if (!address) return '0.000';
    const balanceStr = this.tokenBalances()[address.toLowerCase()];
    if (!balanceStr) return '0.00000000';

    const num = parseFloat(balanceStr);
    if (isNaN(num)) return '0.000';

    if (num >= 1e15) return '>999T';
    if (num >= 1e12) return (num / 1e12).toFixed(2) + 'T';
    if (num >= 1e9) return (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(2) + 'K';
    if (num === 0) return '0.000';
    if (num < 0.00000001 && num > 0) return '<0.00000001';

    return parseFloat(num.toFixed(8)).toString();
  }

  // Swap handling
  private routeDebounceTimer: any;
  public lastChangedAmount: 'from' | 'to' = 'from';
  setMax(target: 'from' | 'to') {
    const token = target === 'from' ? this.fromToken() : this.toToken();
    if (!token || !token.address) return;
    const balance = this.tokenBalances()[token.address];
    if (!balance || parseFloat(balance) <= 0) return;

    // Slight buffer for native token gas fees
    let maxVal = parseFloat(balance);
    if (token.symbol === 'ETH' || token.symbol === 'BNB') {
      maxVal = Math.max(0, maxVal - 0.01);
    }

    const formatted = maxVal.toFixed(6);
    if (target === 'from') {
      this.fromAmount.set(formatted);
      this.onFromAmountChange({ target: { value: formatted } });
    } else {
      this.toAmount.set(formatted);
      this.onToAmountChange({ target: { value: formatted } });
    }
  }

  validateInputAmount(val: string): string {
    let cleaned = val.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    if (parts.length === 2 && parts[1].length > 18) {
      cleaned = parts[0] + '.' + parts[1].substring(0, 18);
    }
    if (parts[0].length > 70) {
      cleaned = parts[0].substring(0, 70) + (parts.length > 1 ? '.' + parts[1] : '');
    }
    return cleaned;
  }

  onFromAmountChange(event: any) {
    let val = event.target.value;
    val = this.validateInputAmount(val);
    event.target.value = val;
    this.fromAmount.set(val);
    this.lastChangedAmount = 'from';
    if (!val || isNaN(parseFloat(val)) || parseFloat(val) <= 0) {
      this.toAmount.set('');
      this.currentSwapRoute.set(null);
      this.priceImpact.set('0.00');
      this.networkFeeUSD.set('0.00');
      this.minimumReceived.set('0.00');
    } else {
      if (this.routeDebounceTimer) clearTimeout(this.routeDebounceTimer);
      this.isFetchingQuote.set(true);
      this.routeDebounceTimer = setTimeout(() => {
        this.calculateRoute();
      }, 500); // 500ms debounce
    }
  }

  onToAmountChange(event: any) {
    let val = event.target.value;
    val = this.validateInputAmount(val);
    event.target.value = val;
    this.toAmount.set(val);
    this.lastChangedAmount = 'to';
    const toVal = parseFloat(val);
    const toT = this.toToken();
    if (isNaN(toVal) || toVal <= 0 || !toT) {
      this.fromAmount.set('');
      this.currentSwapRoute.set(null);
      return;
    }

    if (this.routeDebounceTimer) clearTimeout(this.routeDebounceTimer);
    this.isFetchingQuote.set(true);
    this.routeDebounceTimer = setTimeout(() => {
      this.calculateRoute();
    }, 500);
  }

  swapTokensDirection() {
    const from = this.fromToken();
    const to = this.toToken();
    if (to) {
      this.fromToken.set(to);
      this.toToken.set(from);
      const oldFrom = this.fromAmount();
      const oldTo = this.toAmount();
      this.fromAmount.set(oldTo);
      this.toAmount.set(oldFrom);
    }
  }

  openTokenModal(target: 'from' | 'to') {
    this.tokenModalTarget.set(target);
    this.tokenSearchQuery.set('');
    this.showTokenModal.set(true);
  }

  closeTokenModal() {
    this.showTokenModal.set(false);
  }

  selectToken(token: Token) {
    const target = this.tokenModalTarget();
    if (target === 'from') {
      if (this.toToken()?.symbol === token.symbol) {
        this.toToken.set(this.fromToken());
      } else {
        const validPairs = token.pairs?.map((p: any) => p.tokenAddress?.toLowerCase()) || [];
        const currentTo = this.toToken();
        if (currentTo && currentTo.address && !validPairs.includes(currentTo.address.toLowerCase())) {
          this.toToken.set(null as any);
        }
      }
      this.fromToken.set(token);
    } else {
      if (this.fromToken()?.symbol === token.symbol) {
        this.fromToken.set(this.toToken() || this.tokens()[0]);
      }
      this.toToken.set(token);
    }
    this.closeTokenModal();
    if (this.fromAmount() && this.toToken()) {
      this.calculateRoute();
    }
  }

  async calculateRoute(isBackgroundPoll: boolean = false) {
    if (!isBackgroundPoll) {
      this.isFetchingQuote.set(true);
    }
    const isExactOut = this.lastChangedAmount === 'to';
    const activeAmount = isExactOut ? this.toAmount() : this.fromAmount();

    if (
      !activeAmount ||
      !this.fromToken() ||
      !this.toToken() ||
      !this.fromToken().address ||
      !this.toToken()!.address
    ) {
      if (!isBackgroundPoll) this.isFetchingQuote.set(false);
      return;
    }

    try {
      const activeAmountVal = isExactOut
        ? ethers.parseUnits(activeAmount, this.toToken()?.decimals || 18)
        : ethers.parseUnits(activeAmount, this.fromToken().decimals || 18);

      const NATIVE_ADDR = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';
      const searchTokenOut = this.toToken()!.address!.toLowerCase() === NATIVE_ADDR
        ? (this.toToken()!.wrapAddress || this.toToken()!.address!)
        : this.toToken()!.address!;

      const pairInfo = this.fromToken().pairs?.find(
        (p: any) => p.tokenAddress.toLowerCase() === searchTokenOut.toLowerCase(),
      );
      const isExternal = pairInfo ? pairInfo.liquidityType === 'external' : true;
      const feeTiers = pairInfo?.feeTiers || [];

      let route: any;

      if (isExternal && isExactOut) {
        const reverseRoute = await this.routingService.getBestSwapRoute(
          this.toToken()!.address!,
          this.fromToken().address!,
          activeAmountVal,
          false,
          isExternal,
          feeTiers,
        );

        if (!reverseRoute || !reverseRoute.estimatedOutput) {
          this.triggerNotification('Could not calculate a reverse quote for this pair.', false);
          this.isFetchingQuote.set(false);
          return;
        }

        const estimatedFromAmountVal = reverseRoute.estimatedOutput;
        route = await this.routingService.getBestSwapRoute(
          this.fromToken().address!,
          this.toToken()!.address!,
          estimatedFromAmountVal,
          false,
          isExternal,
          feeTiers,
        );

        route.estimatedInput = estimatedFromAmountVal;
      } else {
        // Standard calculation
        route = await this.routingService.getBestSwapRoute(
          this.fromToken().address!,
          this.toToken()!.address!,
          activeAmountVal,
          isExactOut,
          isExternal,
          feeTiers,
        );
      }

      this.currentSwapRoute.set(route);

      // Calculate derived metrics
      if (isExactOut) {
        if (route.estimatedInput) {
          const inFormatted = ethers.formatUnits(
            route.estimatedInput,
            this.fromToken()?.decimals || 18,
          );
          this.fromAmount.set(parseFloat(inFormatted).toFixed(6));

          // Slippage based min received / max sent
          // For exact out, slippage means you might pay more
          const slippageMultiplier = 1 + this.slippageTolerance() / 100;
          const maxSent = parseFloat(inFormatted) * slippageMultiplier;
          this.minimumReceived.set(maxSent.toFixed(6)); // actually maximum sent in this context

          // Exchange Rate
          const rate = parseFloat(activeAmount) / parseFloat(inFormatted);
          this.exchangeRate.set(
            `1 ${this.fromToken().symbol} = ${rate.toFixed(6)} ${this.toToken()?.symbol}`,
          );
        }
      } else {
        if (route.estimatedOutput) {
          const outFormatted = ethers.formatUnits(
            route.estimatedOutput,
            this.toToken()?.decimals || 18,
          );
          this.toAmount.set(parseFloat(outFormatted).toFixed(6));

          // Slippage based min received
          const slippageMultiplier = 1 - this.slippageTolerance() / 100;
          const minRec = parseFloat(outFormatted) * slippageMultiplier;
          this.minimumReceived.set(minRec.toFixed(6));

          // Exchange Rate
          const rate = parseFloat(outFormatted) / parseFloat(activeAmount);
          this.exchangeRate.set(
            `1 ${this.fromToken().symbol} = ${rate.toFixed(6)} ${this.toToken()?.symbol}`,
          );
        }
      }

      if (route.priceImpact) {
        this.priceImpact.set((parseFloat(route.priceImpact) * 100).toFixed(2));
      } else {
        // Mock impact for native if not calculated directly
        this.priceImpact.set('0.10');
      }

      if (route.gasEstimate) {
        const gasCostInEth = parseFloat(route.gasEstimate) * 15 * 1e-9;
        this.networkFeeUSD.set((gasCostInEth * 3000).toFixed(2));
      }
    } catch (e: any) {
      console.error('Swap Route Error:', e);
      this.currentSwapRoute.set(null);
      this.toAmount.set('');
      this.priceImpact.set('0.00');
      this.networkFeeUSD.set('0.00');

      if (!isBackgroundPoll) {
        const errorMsg = e?.message || '';
        if (errorMsg.includes('No liquidity found') || errorMsg.includes('Unexpected error')) {
          this.triggerNotification('No liquidity provided yet for these tokens.', false);
        }
      }
    } finally {
      if (!isBackgroundPoll) {
        this.isFetchingQuote.set(false);
      }
    }
  }

  setSlippage(val: number) {
    this.slippageTolerance.set(val);
    this.showSlippageDropdown.set(false);
    // Recalculate minimum received immediately
    if (this.toAmount()) {
      const slippageMultiplier = 1 - val / 100;
      const minRec = parseFloat(this.toAmount()) * slippageMultiplier;
      this.minimumReceived.set(minRec.toFixed(6));
    }
  }

  // --- Web3 Interactions --- //

  getSwapValidationState(): { isValid: boolean; message: string } {
    if (this.isFetchingQuote()) {
      return { isValid: false, message: 'Fetching Route...' };
    }
    if (!this.fromAmount() || parseFloat(this.fromAmount()) <= 0) {
      return { isValid: false, message: 'Enter an amount' };
    }
    if (!this.toToken()) {
      return { isValid: false, message: 'Select a token' };
    }
    if (this.fromToken().address === this.toToken()?.address) {
      return { isValid: false, message: 'Invalid token pair' };
    }
    if (this.isWalletConnected()) {
      const balanceStr = this.tokenBalances()[this.fromToken().address!] || '0';
      if (parseFloat(this.fromAmount()) > parseFloat(balanceStr)) {
        return { isValid: false, message: `Insufficient ${this.fromToken().symbol} balance` };
      }
    }
    if (!this.currentSwapRoute() && parseFloat(this.fromAmount()) > 0) {
      return { isValid: false, message: 'Insufficient Liquidity for Trade' };
    }
    return { isValid: true, message: 'Swap' };
  }

  async executeSwap() {
    if (!this.web3Service.isConnected()) {
      this.openConnectModal();
      return;
    }
    const fromVal = parseFloat(this.fromAmount());
    if (isNaN(fromVal) || fromVal <= 0) {
      this.triggerNotification('Please enter a valid swap amount.');
      return;
    }
    if (!this.toToken()) {
      this.triggerNotification('Please select a target token.');
      return;
    }

    try {
      this.isSwapping.set(true);
      this.triggerNotification('Initiating swap... Check your wallet');

      const tokenInAddr = this.fromToken().address!;
      const tokenOutAddr = this.toToken()!.address!;

      const amountIn = ethers.parseUnits(fromVal.toString(), this.fromToken().decimals || 18);

      const NATIVE_ADDR = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';
      const searchTokenOut = tokenOutAddr.toLowerCase() === NATIVE_ADDR
        ? (this.toToken()!.wrapAddress || tokenOutAddr)
        : tokenOutAddr;

      const pairInfo = this.fromToken().pairs?.find(
        (p: any) => p.tokenAddress.toLowerCase() === searchTokenOut.toLowerCase(),
      );
      const isExternal = pairInfo ? pairInfo.liquidityType === 'external' : true;
      const feeTiers = pairInfo?.feeTiers || [];

      await this.web3Service.switchNetwork(this.selectedNetworkId());

      const route = this.currentSwapRoute();
      if (!route) {
        throw new Error("Please wait for the route to calculate before swapping.");
      }

      this.zone.run(() => {
        this.triggerNotification(`Routing...`);
      });

      let tx;

      if (route.type === 'WRAP') {
        tx = await this.web3Service.wrapNative(tokenOutAddr, amountIn);
      } else if (route.type === 'UNWRAP') {
        tx = await this.web3Service.unwrapNative(tokenInAddr, amountIn);
      } else if (route.type === 'DEX') {
        // Native DEX Execution
        const contracts = this.web3Service.getContractsConfig();
        const NATIVE = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';
        const isNativeIn = tokenInAddr.toLowerCase() === NATIVE;
        const isNativeOut = tokenOutAddr.toLowerCase() === NATIVE;

        const routerTokenIn = isNativeIn ? (this.fromToken().wrapAddress || tokenInAddr) : tokenInAddr;
        const routerTokenOut = isNativeOut ? (this.toToken().wrapAddress || tokenOutAddr) : tokenOutAddr;

        await this.web3Service.checkAndApprove(tokenInAddr, contracts.SWAP_ROUTER, amountIn);

        const router = await this.web3Service.getSwapRouter();
        const deadline = Math.floor(Date.now() / 1000) + 60 * 20;

        const dynamicFee = parseInt(route.callData || '3000', 10);

        const params = {
          tokenIn: routerTokenIn,
          tokenOut: routerTokenOut,
          fee: dynamicFee,
          recipient: this.web3Service.address(),
          deadline: deadline,
          amountIn: amountIn,
          amountOutMinimum: 0,
          sqrtPriceLimitX96: 0,
        };

        const msgValue = isNativeIn ? amountIn : 0n;
        tx = await router['exactInputSingle'](params, { value: msgValue });
      } else {
        if (!route.targetAddress || !route.callData) throw new Error('Invalid aggregator route');

        const targetForApproval = route.allowanceTarget || route.targetAddress;
        await this.web3Service.checkAndApprove(tokenInAddr, targetForApproval, amountIn);

        let finalCallData = route.callData;

        if (route.permit2 && route.permit2.eip712) {
          this.triggerNotification('Please sign the Permit2 typed data in your wallet...');
          try {
            const signature = await this.web3Service.signTypedData(
              route.permit2.eip712.domain,
              route.permit2.eip712.types,
              route.permit2.eip712.message,
            );

            const signatureHex = signature.startsWith('0x') ? signature.slice(2) : signature;
            const signatureLengthInBytes = signatureHex.length / 2;
            const lengthHex = signatureLengthInBytes.toString(16).padStart(64, '0');

            finalCallData = finalCallData + lengthHex + signatureHex;
            this.triggerNotification('Signature accepted. Executing swap...');
          } catch (err: any) {
            throw new Error('User denied message signature or signature failed.');
          }
        }

        const NATIVE = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';
        let txValue = route.value || '0';
        if (tokenInAddr.toLowerCase() === NATIVE) {
          txValue = amountIn.toString();
        }

        tx = await this.web3Service.sendRawTransaction(
          route.targetAddress,
          finalCallData,
          txValue,
        );
      }

      // Capture current state values BEFORE waiting for transaction (in case user clicks reverse while waiting)
      const capturedFromSymbol = this.fromToken().symbol;
      const capturedToSymbol = this.toToken()!.symbol;
      const capturedFromAmount = parseFloat(this.fromAmount());
      const capturedToAmount = parseFloat(this.toAmount());
      const capturedUsdVal = (capturedFromAmount * (this.fromToken().rate || 0)) || (capturedToAmount * (this.toToken()?.rate || 0)) || 0;
      const capturedNetworkFee = parseFloat(this.networkFeeUSD());
      const capturedPlatformName = route.platformName;

      this.triggerNotification('Transaction submitted! Waiting for confirmation...');
      const receipt = await this.web3Service.waitForTransaction(tx.hash);

      if (!receipt) {
        throw new Error('Transaction confirmation timed out. Please check your wallet history.');
      }

      try {
        const payload = {
          walletAddress: this.web3Service.address(),
          txHash: receipt.hash || tx.hash,
          fromTokenAddress: tokenInAddr,
          fromTokenSymbol: capturedFromSymbol,
          toTokenAddress: tokenOutAddr,
          toTokenSymbol: capturedToSymbol,
          amountIn: capturedFromAmount,
          amountOut: capturedToAmount,
          usdValue: capturedUsdVal,
          networkFeeUSD: capturedNetworkFee,
          swapType: route.type === 'DEX' ? 'INTERNAL' : 'EXTERNAL',
          platformName: capturedPlatformName,
        };
        await firstValueFrom(this.http.post(`${environment.apiUrl}/user/swap/record`, payload));

        // Unified Activity Log
        await firstValueFrom(this.activityService.recordActivity({
          txHash: receipt.hash || tx.hash,
          chainId: this.web3Service.chainId() || 1,
          actionType: 'SWAP',
          status: 'Success',
          payload: {
            amountIn: capturedFromAmount,
            amountOut: capturedToAmount,
            fromTokenSymbol: capturedFromSymbol,
            toTokenSymbol: capturedToSymbol,
            usdValue: capturedUsdVal,
            feeUSD: capturedNetworkFee
          },
          platformName: capturedPlatformName
        }));
      } catch (err) {
        console.error('Failed to record swap in backend', err);
      }

      this.zone.run(() => {
        this.triggerNotification(`Swapped ${capturedFromAmount} ${capturedFromSymbol} successfully!`);
        this.fromAmount.set('');
        this.toAmount.set('');
        this.currentSwapRoute.set(null);
        this.priceImpact.set('0.00');
      });

      // Refresh balances so the frontend shows the new updated token balances!
      try {
        await this.fetchBalances(true);
      } catch (err) {
        console.warn('Failed to quietly refresh balances after swap', err);
      }
      this.zone.run(() => {
        this.networkFeeUSD.set('0.00');
        this.minimumReceived.set('0.00');
        this.exchangeRate.set('0.00');
      });
      this.fetchBalances();
      setTimeout(() => this.fetchBalances(), 4000);
    } catch (e: any) {
      console.error(e);
      this.zone.run(() => {
        this.triggerNotification(this.handleError(e, 'Swap failed'), false);
      });
    } finally {
      this.zone.run(() => {
        this.isSwapping.set(false);
      });
    }
  }

  handleError(e: any, defaultMsg: string): string {
    console.error('[Transaction Error]', e);

    if (e?.code === 'ACTION_REJECTED' || e?.code === 4001 || e?.info?.error?.code === 4001 || e?.error?.code === 4001) {
      return 'Transaction was cancelled by the user.';
    }

    const rawMsg = (e?.reason || e?.shortMessage || e?.message || String(e)).toLowerCase();
    if (rawMsg.includes('user rejected') || rawMsg.includes('rejected transaction') || rawMsg.includes('user denied') || rawMsg.includes('action_rejected')) {
      return 'Transaction was cancelled by the user.';
    }
    if (rawMsg.includes('bad address checksum')) {
      return 'Transaction failed: Invalid contract address configuration.';
    }
    if (rawMsg.includes('insufficient') || rawMsg.includes('exceeds balance')) {
      return 'Transaction failed: Insufficient balance.';
    }
    if (rawMsg.includes('no data present') || rawMsg.includes('require(false)')) {
      return 'Transaction failed: Insufficient wrapped token balance or allowance. Please ensure you have wrapped your native tokens (e.g., WMATIC) before providing liquidity.';
    }

    let cleanMsg = defaultMsg ? defaultMsg.trim() : 'Transaction failed.';
    if (cleanMsg.endsWith('.')) {
      cleanMsg = cleanMsg.slice(0, -1);
    }

    return `${cleanMsg}. Please try again later.`;
  }

  openStakingModal(pool: StakingPool) {
    this.selectedStakedPool.set(pool);
    this.stakingAmountInput.set('');
    this.showStakingModal.set(true);
  }

  closeStakingModal() {
    this.showStakingModal.set(false);
  }

  executeStake() {
    if (!this.isWalletConnected()) {
      this.closeStakingModal();
      this.openConnectModal();
      return;
    }
    const amt = parseFloat(this.stakingAmountInput());
    if (isNaN(amt) || amt <= 0) {
      this.triggerNotification('Please enter a valid amount to stake.');
      return;
    }
    const pool = this.selectedStakedPool();
    this.triggerNotification(
      `Staked ${amt} ${pool?.tokenSymbol} in the ${pool?.name} pool successfully!`,
    );
    this.closeStakingModal();
  }

  private navigateToRoute(id: string) {
    const routeMap: Record<string, string> = {
      swap: '/swap',
      launchpad: '/launchpad',
      portfolio: '/portfolio',
      staking: '/staking',
      'yield-farming': '/yield-farming',
      explore: '/explore',
      advantages: '/advantages',
      about: '/about',
      contact: '/contact',
      'privacy-policy': '/privacy-policy',
      'terms-and-conditions': '/terms-and-conditions',
      history: '/history',
      bankless: '/bankless',
    };
    if (routeMap[id]) {
      this.router.navigate([routeMap[id]]);
    } else if (id === 'portfolio') {
      this.router.navigate(['/portfolio']);
    } else if (id === 'liquidity') {
      this.router.navigate(['/liquidity']);
    } else {
      this.router.navigate(['/']);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  goToSwapWithParams() {
    const fromSymbol = this.fromToken()?.symbol || '';
    const toSymbol = this.toToken()?.symbol || '';
    const amount = this.fromAmount() || '';

    this.activeFeatureTab.set('swap');
    this.activeNavLink.set('swap');

    const queryParams: any = {};
    if (fromSymbol && fromSymbol !== '---') queryParams.from = fromSymbol;
    if (toSymbol && toSymbol !== '---') queryParams.to = toSymbol;
    if (amount) queryParams.amount = amount;

    this.router.navigate(['/swap'], { queryParams });
  }

  selectTab(tab: string) {
    const featureTab = tab === 'yield-farming' ? 'farms' : tab;
    this.activeFeatureTab.set(featureTab);
    this.activeNavLink.set(tab === 'farms' ? 'yield-farming' : tab);
    this.navigateToRoute(tab);
  }

  selectNav(id: string) {
    this.activeNavLink.set(id);
    this.activeFeatureTab.set(id === 'yield-farming' ? 'farms' : id);
    this.navigateToRoute(id);
  }

  selectFooterProduct(id: string) {
    if (id === 'ethereum') {
      document
        .getElementById('ethereum-network')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (id === 'farms') {
      this.selectTab('yield-farming');
      return;
    }
    this.selectTab(id);
  }

  selectFooterCompany(id: string) {
    this.router.navigate([`/${id}`]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  selectFooterLegal(id: string) {
    this.router.navigate([`/${id}`]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  quickSwapToken(token: Token) {
    this.skipNextClear = true;
    this.toToken.set(token);
    this.fromAmount.set('1.00');
    this.selectTab('swap');
    this.triggerNotification(`Selected ${token.symbol} for quick swapping!`);
  }

  quickStakePool(pool: StakingPool) {
    this.openStakingModal(pool);
  }

  claimRewards() {
    const rewards = this.claimableRewards();
    if (rewards <= 0) {
      this.triggerNotification('No claimable rewards accumulated yet.');
      return;
    }
    this.triggerNotification(
      `Successfully claimed ${rewards.toFixed(6)} DXOR rewards to your wallet!`,
    );
    this.claimableRewards.set(0.0);
  }

  openLaunchpadModal(project: LaunchpadProject) {
    if (!this.isWalletConnected()) {
      this.openConnectModal();
      return;
    }
    this.selectedLaunchpadProject.set(project);
    this.commitLaunchpadAmount.set('');
    this.showLaunchpadModal.set(true);
  }

  closeLaunchpadModal() {
    this.showLaunchpadModal.set(false);
  }

  executeLaunchpadCommit() {
    const amt = parseFloat(this.commitLaunchpadAmount());
    if (isNaN(amt) || amt <= 0) {
      this.triggerNotification('Please enter a valid amount.');
      return;
    }
    const project = this.selectedLaunchpadProject();
    if (!project) return;
    this.triggerNotification(`Successfully committed ${amt} BNB to the ${project.name} launchpad!`);

    this.launchpadProjects.update((projects) =>
      projects.map((p) => {
        if (p.symbol === project.symbol) {
          const newRaised = p.raised + amt;
          const newProgress = Math.min(100, parseFloat(((newRaised / p.target) * 100).toFixed(2)));
          return { ...p, raised: newRaised, progress: newProgress };
        }
        return p;
      }),
    );

    this.closeLaunchpadModal();
  }

  getTokenBySymbol(symbol: string): MarketToken | undefined {
    return this.marketTokens().find((t) => t.symbol.toUpperCase() === symbol.toUpperCase());
  }


}